import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Box, Stack, Chip, Tabs, Tab } from "@mui/material";
import CustomLayout from "../../../../components/layout";
import PrimaryButton from "../../../../components/buttons/primary-button";
import LoanInfoCard from "../../../../components/info-card";
import QualifyAccordion, { inputStepStatus } from "../../../../components/qualify-accordion";
import CustomAlert from "../../../../components/custom-alert";
import { TabPanel } from "../../../../components/tab-panel";
import EmptyTextContainer from "../../../../components/empty-text-container";
import RequiredError from "../../../../components/required-error";
import LoaderModal from "../../../../components/modal/loader-modal";
import SuccessModal from "../../../../components/modal/success-modal";
import CommanErrorModal from "../../../../components/modal/common-error-modal";
import AuraInProgressBanner from "../../../../components/aura-in-progress-banner";
import RegisteredProgramCard from "../../../../components/registered-program-card";
import Heading5 from "../../../../components/typography/heading-05";
import Heading6 from "../../../../components/typography/heading-06";
import Body3 from "../../../../components/typography/body-03";
import ProductInputsPanel from "../aura/ProductInputsPanel";
import { useProductInputsForm } from "../aura/useProductInputsForm";
import EligibleFilter from "../../../../components/loan-form-card/eligible-filter";
import IneligibleFilter from "../../../../components/loan-form-card/ineligible-filter";
import { useGetLoanMutation } from "../../../../features/get-loan-data/getLoanApiSlice";
import { useGetEligibleProductsMutation } from "../../../../features/get-eligible-products/getEligibleProductsApiSlice";
import { useGetEligibleProductDetailsMutation } from "../../../../features/get-eligible-product-details/getEligibleProductDetailsApiSlice";
import { useGetIneligibleProductsMutation } from "../../../../features/get-ineligible-products/getIneligibleProductsApiSlice";
import { useUpdateFieldsMutation } from "../../../../features/update-fields/updateFieldsApiSlice";
import { selectUserDetails, selectSelectedOrganization } from "../../../../features/auth/authSlice";
import { selectImpersonatedUser } from "../../../../features/admin/impersonated-user/impersonatedUserSlice";
import { selectedUserWithLoanChannel } from "../../../../utils/common-helpers";
import { isRealLoanGuid } from "../../../../utils/normalize-xml-for-create-loan";
import {
	setSelectedProgram,
	setCurrentStage,
	selectCurrentStage,
	selectLoanGuid,
	selectIncomeDocumentationType,
	selectSectionStatuses,
	setIncomeDocumentationType,
} from "../../../../features/current-loan/currentLoanSlice";
import { useSyncAuraProgress } from "../../../../features/current-loan/useSyncAuraProgress";
import useAuraInProgress from "../../../../hooks/useAuraInProgress";
import { useErrorMessages } from "../../../../utils/error-messages.constants";
import { getConflictPopup, isConflictError, isObPushInQueue } from "../../../../utils/conflict-error";
import styles from "./index.module.scss";
import { ENCOMPASS_FIELD_IDS, PRODUCT_INPUTS_FIELD_IDS } from "../../../../utils/aura.constants";
import {
	buildEligibleSearchFormData,
	buildProductInputsSaveValues,
	mapProductInputsFromFields,
} from "../../../../utils/product-inputs-encompass";
import { isDSCRstyle } from "../../../../utils/income-documentation-helpers";
import { getPostQualifyStage } from "../../../../utils/qualify-entry-path";
import { useFieldMutation } from "../../../../features/field-values/fieldValueApiSlice";

const a11yProps = (index) => ({
	id: `qualify-non-aura-tab-${index}`,
	"aria-controls": `qualify-non-aura-tabpanel-${index}`,
});

const QualifyNonAuraPage = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const loanGuid = useSelector(selectLoanGuid);
	useSyncAuraProgress();
	const currentStage = useSelector(selectCurrentStage);

	// Assert "qualify" on mount regardless of navigation method — mirrors the same fix
	// in dashboard/index.jsx and qualify/aura/index.jsx.
	useEffect(() => {
		if (loanGuid && currentStage !== "qualify") dispatch(setCurrentStage("qualify"));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [loanGuid]);

	const userDetails = useSelector(selectUserDetails);
	const impersonatedUser = useSelector(selectImpersonatedUser);
	const selectedOrganization = useSelector(selectSelectedOrganization);

	const [getLoan] = useGetLoanMutation();
	const [getEligibleProducts] = useGetEligibleProductsMutation();
	const [getEligibleProductDetails] = useGetEligibleProductDetailsMutation();
	const [getIneligibleProducts] = useGetIneligibleProductsMutation();
	const [updateFields] = useUpdateFieldsMutation();
	const [field] = useFieldMutation();
	const incomeDocumentationType = useSelector(selectIncomeDocumentationType);
	const sectionStatuses = useSelector(selectSectionStatuses);

	React.useEffect(() => {
		if (!loanGuid) navigate("/current-loan");
	}, [loanGuid, navigate]);

	// Read-only Property strip — same loan-level fetch-on-mount pattern the AURA page
	// uses (getLoan → property), since Optimal Blue needs propertyType/occupancy/state
	// and this manual path has no XML/credit-reissue data source of its own.
	const [displayData, setDisplayData] = useState({});
	useEffect(() => {
		if (!isRealLoanGuid(loanGuid)) return;
		getLoan({ loanid: loanGuid })
			.unwrap()
			.then((loanData) => {
				const property = loanData?.response?.property;
				if (!property) return;
				setDisplayData((prev) => ({
					...prev,
					propertyType: property.propertyType || prev.propertyType,
					occupancy: property.occupancy || prev.occupancy,
					state: property.state || prev.state,
					county: property.county || prev.county,
				}));
			})
			.catch(() => {});
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [loanGuid]);

	// Shared Product & pricing inputs form — same Redux-backed config-driven form
	// AURA Panel B uses (currentLoanSlice.productInputs), so values entered on either
	// path are preserved when switching paths — except what the AURA path filled in from
	// its credit re-issue: this path has none (FICO comes from the XML or the broker).
	const {
		formState: piFormState,
		setField: piSetField,
		hydrateFromEncompass: piHydrateFromEncompass,
		errors: piErrors,
		setErrors: piSetErrors,
		submitted: piSubmitted,
		setSubmitted: piSetSubmitted,
		validateForm: piValidateForm,
	} = useProductInputsForm({ displayData });

	const dtiFromFields = (fields) =>
		(fields || []).find((f) => String(f?.fieldId) === ENCOMPASS_FIELD_IDS.DTI)?.value ?? "";

	// Reads DTI (742) from Encompass into the form; resolves the value ("" when not calculated).
	const readEncompassDti = async () => {
		const res = await field({ loan_guid: loanGuid, fields: ENCOMPASS_FIELD_IDS.DTI }).unwrap();
		const dti = dtiFromFields(res?.response);
		piHydrateFromEncompass({ debtToIncomeRatio: dti });
		return dti;
	};

	// Same as the AURA page's Step B: load the saved product inputs from Encompass (and the
	// loan's DTI, field 742, unless DSCR) so the form starts from the loan, not blanks.
	useEffect(() => {
		if (!isRealLoanGuid(loanGuid)) return;
		const readDti = !isDSCRstyle(incomeDocumentationType);
		const ids = readDti ? [...PRODUCT_INPUTS_FIELD_IDS, ENCOMPASS_FIELD_IDS.DTI] : PRODUCT_INPUTS_FIELD_IDS;
		field({ loan_guid: loanGuid, fields: ids.join(",") })
			.unwrap()
			.then((res) => {
				const hydrated = mapProductInputsFromFields(res?.response);
				// DTI is exactly Encompass's 742 ("" when not calculated), as on the AURA page.
				if (readDti) hydrated.debtToIncomeRatio = dtiFromFields(res?.response);
				piHydrateFromEncompass(hydrated);
				if (!incomeDocumentationType && hydrated.incomeVerificationType) {
					dispatch(setIncomeDocumentationType(hydrated.incomeVerificationType));
				}
			})
			.catch(() => {
				// best-effort, as on the AURA page — the form keeps its current values
			});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [loanGuid]);

	// Panel A
	const [panelAOpen, setPanelAOpen] = useState(true);
	const [panelAStatus, setPanelAStatus] = useState("active");
	const [panelAError, setPanelAError] = useState("");
	const [searching, setSearching] = useState(false);

	// Panel B — Eligible / Ineligible programs (real Optimal Blue data)
	const [panelBOpen, setPanelBOpen] = useState(false);
	const [panelBStatus, setPanelBStatus] = useState("pending");
	const [eligibleProducts, setEligibleProducts] = useState({});
	const [ineligibleProducts, setIneligibleProducts] = useState([]);
	const [eligibleProductDetails, setEligibleProductDetails] = useState({});
	const [expandedProduct, setExpandedProduct] = useState("");
	const [selectedRow, setSelectedRow] = useState({});
	const [selectedProduct, setSelectedProduct] = useState({});
	const [tabValue, setTabValue] = useState(0);
	const handleChangeTab = (event, newValue) => setTabValue(newValue);
	const [panelBError, setPanelBError] = useState("");

	// Program-registered confirmation (Panel B → confirmation before continuing)
	const [registered, setRegistered] = useState(false);

	// Locked while an AURA run is in flight; a 409 opens a popup with the backend's message.
	const { locked: auraLocked } = useAuraInProgress(loanGuid);
	const { getErrorMessage } = useErrorMessages();
	const [conflictPopup, setConflictPopup] = useState(null);

	const getMissingSearchFields = () => {
		const missing = [];
		if (!piFormState.loanAmount) missing.push("Loan Amount");
		if (!piFormState.qualifyingCreditScore) missing.push("Qualifying Credit Score (FICO)");
		if (!piFormState.incomeVerificationType) missing.push("Income Verification Type");
		if (!piFormState.propertyType) missing.push("Property Type");
		if (!piFormState.occupancy) missing.push("Occupancy");
		if (!piFormState.zipcode) missing.push("Zipcode");
		if (!displayData.state) missing.push("State");
		return missing;
	};

	const handleToggleExpand = (productId) => {
		if (expandedProduct === productId) {
			setExpandedProduct("");
			return;
		}
		setExpandedProduct(productId);
		getEligibleProductDetails({
			ob_business_channel_id: eligibleProducts?.data?.ob_business_channel_id,
			ob_originator_id: eligibleProducts?.data?.ob_originator_id,
			ob_loan_id: eligibleProducts?.data?.ob_loan_id,
			search_id: eligibleProducts?.data?.searchId,
			product_id: productId,
		})
			.unwrap()
			.then(setEligibleProductDetails)
			.catch(() => {});
	};

	const handleSearchPrograms = async () => {
		if (auraLocked) return;
		piSetSubmitted(true);
		const piFormErrors = piValidateForm();
		// Actually highlight the offending fields (previously computed but never
		// applied, so nothing ever turned red despite the banner text).
		piSetErrors(piFormErrors);

		if (Object.keys(piFormErrors).length > 0) {
			setPanelAError("Please fill in all required fields highlighted above.");
			return;
		}
		setPanelAError("");

		const missingSearchFields = getMissingSearchFields();
		if (missingSearchFields.length > 0) {
			setPanelAError(
				`Missing required field(s) for pricing search: ${missingSearchFields.join(", ")}. ` +
					`Please confirm these on the loan (property details, income type, credit score) before searching.`
			);
			return;
		}

		setSearching(true);
		try {
			const effectiveUserWithChannel = selectedUserWithLoanChannel(
				userDetails,
				impersonatedUser,
				selectedOrganization
			);

			// Optimal Blue reads most inputs off the saved loan, so the whole Step B form is
			// saved first (the same values as the AURA page), then the search is run with the
			// form's own citizenship / income type / DSCR / DTI.
			await updateFields({
				loan_guid: loanGuid,
				field_values: buildProductInputsSaveValues(piFormState),
			}).unwrap();

			// Saving can change Encompass's DTI (e.g. a new loan amount changes the payment), so
			// read 742 again and price with the value Encompass now has.
			const searchInputs = isDSCRstyle(incomeDocumentationType)
				? piFormState
				: { ...piFormState, debtToIncomeRatio: await readEncompassDti() };

			const loanDetails = await getLoan({ loanid: loanGuid }).unwrap();
			const formData = buildEligibleSearchFormData({
				s: searchInputs,
				loanGuid,
				loanNumber: loanDetails?.response?.loanNumber,
				user: effectiveUserWithChannel,
				selectedOrganization,
			});

			const eligibleProductList = await getEligibleProducts({
				formData,
				selectedOrganization,
				userDetails,
			}).unwrap();
			setEligibleProducts(eligibleProductList);

			if (eligibleProductList?.status?.toLowerCase()?.includes("success")) {
				const ineligibleProductList = await getIneligibleProducts({
					ob_business_channel_id: eligibleProductList?.data?.ob_business_channel_id,
					ob_originator_id: eligibleProductList?.data?.ob_originator_id,
					ob_loan_id: eligibleProductList?.data?.ob_loan_id,
					searchId: eligibleProductList?.data?.searchId,
				}).unwrap();
				setIneligibleProducts(ineligibleProductList?.data?.ineligibleProducts || []);
			}
			// Only move on to the programs once the search succeeded — on a failure the
			// error shows here in Step A instead of an empty program list.
			setPanelAStatus("complete");
			setPanelAOpen(false);
			setPanelBStatus("active");
			setPanelBOpen(true);
		} catch (err) {
			// Optimal Blue's transient 409 "Push is in queue." is not a loan conflict.
			if (isObPushInQueue(err)) setConflictPopup(getErrorMessage("OB_PUSH_IN_QUEUE"));
			else if (isConflictError(err)) setConflictPopup(getConflictPopup(err, getErrorMessage));
			else setPanelAError(err?.data?.detail || "Unable to fetch eligible programs. Please try again.");
		} finally {
			setSearching(false);
		}
	};

	const selectedProductParent = eligibleProducts?.data?.products?.find(
		(p) => p?.productId === expandedProduct
	);

	// Float/Lock decision now happens once, later, on Submit's pre-submission step —
	// this just registers the selected program.
	const handleConfirm = () => {
		if (auraLocked) return;
		if (Object.keys(selectedRow).length === 0) {
			setPanelBError("Please select a program.");
			return;
		}
		const prog = {
			...selectedRow,
			id: expandedProduct,
			name: selectedProductParent?.productName,
		};
		dispatch(setSelectedProgram(prog));
		// Manual path: Submit reads the loan's real Minimum Submission conditions
		// (PathC-4.01) — nothing is seeded here.
		setRegistered(true);
	};

	// Straight to Submit when every Dashboard section is already complete.
	const postQualifyStage = getPostQualifyStage(sectionStatuses);
	const handleContinueToDashboard = () => {
		dispatch(setCurrentStage(postQualifyStage));
		navigate(`/current-loan/${postQualifyStage}`);
	};

	return (
		<CustomLayout>
			<LoaderModal open={searching} />
			<SuccessModal
				open={registered}
				handleClose={handleContinueToDashboard}
				text="Program registered. No automated eligibility was run — a manual underwriter review will apply after submission."
				confirmText={postQualifyStage === "submit" ? "Continue to Submit →" : "Continue to Dashboard →"}
			>
				<RegisteredProgramCard
					program={{ ...selectedRow, name: selectedProductParent?.productName }}
					title="Your registered program & pricing"
				/>
			</SuccessModal>
			<CommanErrorModal
				isOpen={!!conflictPopup}
				onClose={() => setConflictPopup(null)}
				title={conflictPopup?.title}
				message={conflictPopup?.message}
				errorCode={conflictPopup?.code}
			/>
			<Box className={styles.pageRoot}>
				<LoanInfoCard loanId={loanGuid} />
				<AuraInProgressBanner show={auraLocked} />
				<Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
					<Stack direction="column">
						<Heading5 text="Step 3: Qualify" extraClass={styles.pageTitle} />
						<Body3 text="Complete qualification sub-steps" extraClass={styles.subTitle} />
					</Stack>
					<Stack direction="row" alignItems="center" gap={1}>
						<Chip label="QUALIFICATION ENGINE" size="small" className={styles.qeLabel} />
						<Chip label="Non-AURA path" size="small" className={styles.noAuraBadge} />
						<Box
							component="span"
							className={styles.switchLink}
							onClick={() => navigate("/current-loan/qualify/path-select")}
						>
							Switch path →
						</Box>
					</Stack>
				</Stack>

				{/* Stepper A/B — labels/subtitles match the CLEARpath prototype's manualHeader() */}
				<Stack direction="row" alignItems="flex-start" justifyContent="space-between" mb={3} className={styles.stepper}>
					{[
						{ letter: "A", label: "Product inputs", sub: "Optimal Blue parameters" },
						{ letter: "B", label: "Program selection", sub: "Choose eligible program" },
					].map(({ letter, label, sub }, idx) => {
						const isDone = (idx === 0 && panelAStatus === "complete") || (idx === 1 && panelBStatus === "complete");
						const isActive = (idx === 0 && panelAStatus === "active") || (idx === 1 && panelBStatus === "active");
						return (
							<Box key={letter} className={styles.stepNode}>
								{idx === 0 && (
									<Box className={`${styles.stepLine} ${panelAStatus === "complete" ? styles.stepLineDone : ""}`} />
								)}
								<Stack direction="column" alignItems="center" gap={0.5}>
									<Box className={`${styles.stepCircle} ${isDone ? styles.stepDone : isActive ? styles.stepActive : styles.stepPending}`}>
										{letter}
									</Box>
									<span className={styles.stepLabel}>{label}</span>
									<span className={styles.stepSub}>{sub}</span>
								</Stack>
							</Box>
						);
					})}
				</Stack>

				{/* Accordion A — Product Inputs */}
				<Box mb={2}>
					<QualifyAccordion
						stepLabel="A"
						title="Product & pricing inputs (manual credit)"
						status={inputStepStatus(panelAStatus)}
						isOpen={panelAOpen}
						onToggle={() => setPanelAOpen((v) => !v)}
					>
						<CustomAlert
							subject="Manual credit entry"
							details="No credit re-issue. Enter credit profile manually below. All fields are editable."
							severity="info"
						/>

						<ProductInputsPanel
							formState={piFormState}
							setField={piSetField}
							errors={piErrors}
							submitted={piSubmitted}
						/>

						{panelAError && <RequiredError text={panelAError} />}
						<PrimaryButton text="Search programs →" onClick={handleSearchPrograms} disabled={auraLocked} />
					</QualifyAccordion>
				</Box>

				{/* Accordion B — Programs + Pricing */}
				<Box mb={3}>
					<QualifyAccordion
						stepLabel="B"
						title="Programs + pricing"
						status={inputStepStatus(panelBStatus)}
						isOpen={panelBOpen}
						onToggle={() => panelBStatus !== "pending" && setPanelBOpen((v) => !v)}
					>
						<CustomAlert
							subject="Indicative pricing"
							details="Showing available programs from Optimal Blue. No automated AURA eligibility is run on this path — a manual underwriter review will apply after submission."
							severity="info"
						/>
						<Stack direction="column" mb={1}>
							<Tabs
								value={tabValue}
								onChange={handleChangeTab}
								aria-label="qualify non-aura pricing tabs"
								TabIndicatorProps={{ style: { backgroundColor: "#49787F" } }}
							>
								<Tab
									label={
										<Heading6
											text={`Eligible Pricing Results (${eligibleProducts?.data?.products?.length || 0})`}
											fontType="semibold"
										/>
									}
									{...a11yProps(0)}
								/>
								<Tab
									label={
										<Heading6
											text={`Ineligible Pricing Results (${ineligibleProducts?.length || 0})`}
											fontType="semibold"
										/>
									}
									{...a11yProps(1)}
								/>
							</Tabs>
						</Stack>

						<TabPanel index={0} value={tabValue}>
							{eligibleProducts?.data?.products?.length > 0 ? (
								<EligibleFilter
									eligibleProducts={eligibleProducts?.data?.products}
									expandedProduct={expandedProduct}
									toggleExpand={handleToggleExpand}
									eligibleProductDetails={eligibleProductDetails?.data}
									selectedRow={selectedRow}
									setSelectedRow={setSelectedRow}
									setSelectedProduct={setSelectedProduct}
									toggleDrawer={() => {}}
								/>
							) : (
								<EmptyTextContainer
									headerText="No products available"
									bodyText="Please click the Ineligible Pricing Results tab above to see why no products are available."
								/>
							)}
						</TabPanel>

						<TabPanel index={1} value={tabValue}>
							{ineligibleProducts?.length > 0 ? (
								<IneligibleFilter ineligibleProducts={ineligibleProducts} />
							) : (
								<EmptyTextContainer
									headerText="No products available"
									bodyText="There are no ineligible products available for this loan."
								/>
							)}
						</TabPanel>

						{/* Float/Lock decision now happens once, later, on Submit's
						    pre-submission "Float & Submit" / "Request Lock & Submit" step. */}
						{panelBError && <RequiredError text={panelBError} />}
						<PrimaryButton
							text="Register program & continue →"
							onClick={handleConfirm}
							disabled={auraLocked}
							extraClass={styles.mt}
						/>
					</QualifyAccordion>
				</Box>

				<Box className={styles.disclaimer}>
					Not a commitment to lend. All loans subject to underwriting approval.
				</Box>
			</Box>
		</CustomLayout>
	);
};

export default QualifyNonAuraPage;
